import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Grid,
} from '@mui/material';
import {
  ShortText,
  Subject,
  CheckCircleOutline,
  CheckBox,
  ArrowDropDown,
  CalendarToday,
  Timer,
  Phone,
  Email,
  Numbers,
  Schedule,
} from '@mui/icons-material';
import providerFormService from '../../services/providerFormService';

const FIELD_ICONS = {
  SHORT_TEXT: <ShortText fontSize="small" />,
  MULTILINE_TEXT: <Subject fontSize="small" />,
  YES_NO: <CheckCircleOutline fontSize="small" />,
  CHECKBOXES: <CheckBox fontSize="small" />,
  DROPDOWN: <ArrowDropDown fontSize="small" />,
  DATE: <CalendarToday fontSize="small" />,
  DURATION: <Timer fontSize="small" />,
  PHONE: <Phone fontSize="small" />,
  EMAIL: <Email fontSize="small" />,
  NUMBER: <Numbers fontSize="small" />,
  TIME: <Schedule fontSize="small" />,
};

const formatValue = (type, value) => {
  if (value === null || value === undefined || value === '') return '—';
  switch (type) {
    case 'YES_NO':
      return value === 'true' || value === true ? 'Igen' : 'Nem';
    case 'CHECKBOXES':
      return Array.isArray(value) ? value.join(', ') : value.split(',').map(s => s.trim()).filter(Boolean).join(', ');
    case 'DATE':
      try {
        return new Date(value).toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' });
      } catch {
        return value;
      }
    default:
      return String(value);
  }
};

const FieldResponseRow = ({ label, type, value }) => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
      <Box sx={{ color: 'text.disabled', display: 'flex' }}>
        {FIELD_ICONS[type] || <ShortText fontSize="small" />}
      </Box>
      <Typography variant="caption" color="text.secondary" fontWeight={500}>
        {label}
      </Typography>
    </Box>
    <Typography variant="body2" sx={{ pl: 3, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {formatValue(type, value)}
    </Typography>
  </Box>
);

const FormResponseViewer = ({ formResponseId, bookingId }) => {
  const [formResponse, setFormResponse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!formResponseId && !bookingId) {
      setError('Nincs megadva form válasz azonosító.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const fetch = formResponseId
      ? providerFormService.getFormResponseById(formResponseId)
      : providerFormService.getFormResponseByBookingId(bookingId);

    fetch
      .then(response => {
        if (response.success) {
          setFormResponse(response.data);
        } else {
          setError(response.message || 'Form válasz lekérése sikertelen.');
        }
      })
      .catch(() => setError('Hiba történt a form válasz lekérése során.'))
      .finally(() => setLoading(false));
  }, [formResponseId, bookingId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, py: 4 }}>
        <CircularProgress size={24} />
        <Typography color="text.secondary">Betöltés...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!formResponse) {
    return <Alert severity="info">Nincs megjeleníthető form válasz.</Alert>;
  }

  const { providerForm, fieldResponses } = formResponse;
  const sortedResponses = [...(fieldResponses || [])].sort(
    (a, b) => (a.formField?.order ?? 0) - (b.formField?.order ?? 0)
  );

  return (
    <Box>
      {providerForm?.title && (
        <>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {providerForm.title}
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </>
      )}

      <Grid container spacing={0}>
        {sortedResponses.map(fr => (
          <Grid key={fr.id} item xs={12} sm={fr.formField?.type === 'MULTILINE_TEXT' ? 12 : 6}>
            <FieldResponseRow
              label={fr.formField?.label || 'Ismeretlen mező'}
              type={fr.formField?.type || 'SHORT_TEXT'}
              value={fr.value}
            />
          </Grid>
        ))}
      </Grid>

      {sortedResponses.length === 0 && (
        <Alert severity="info">Nincs kitöltött mező ebben a formban.</Alert>
      )}

      <Box sx={{ mt: 2 }}>
        <Chip
          label={`Beküldve: ${formResponse.submittedAt ? new Date(formResponse.submittedAt).toLocaleString('hu-HU') : 'Ismeretlen'}`}
          size="small"
          variant="outlined"
        />
      </Box>
    </Box>
  );
};

export default FormResponseViewer;
